import { z } from "zod";
import {
	type AuditLogDisplay,
	AuditLogDisplaySchema,
	type ServiceResult,
	ServiceResultSchema,
} from "@/services/job/job.types";

export const ENTITY_TYPE_CLASS_WORK = "work";
export const ENTITY_TYPE_CLASS_ASSET = "asset";

export const StatusCategoryTypeDisplaySchema = z
	.object({
		code: z.string(),
		name: z.string(),
		description: z.string().optional(),
	})
	.passthrough();

export type StatusCategoryTypeDisplay = z.infer<
	typeof StatusCategoryTypeDisplaySchema
>;

/** Nested on GET status-lifecycle list/detail; create/filter still use string `code`. */
export const EntityTypeClassFieldSchema =
	StatusCategoryTypeDisplaySchema.nullable().optional();

export type EntityTypeClassField = z.infer<typeof EntityTypeClassFieldSchema>;

export function getEntityTypeClassLabel(
	entityTypeClass: EntityTypeClassField | undefined,
): string | null {
	if (entityTypeClass == null) return null;
	const name = entityTypeClass.name?.trim();
	if (name && name.length > 0) return name;
	const code = entityTypeClass.code?.trim();
	return code && code.length > 0 ? code : null;
}

export function getEntityTypeClassCode(
	entityTypeClass: EntityTypeClassField | undefined,
): string | null {
	if (entityTypeClass == null) return null;
	const code = entityTypeClass.code?.trim();
	return code && code.length > 0 ? code : null;
}

/**
 * POST /workflows/status-lifecycles body — name, description, and optional `organizationId`
 * for org-scoped lifecycles (null / omit for platform).
 */
export const CreateStatusLifecycleInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	entityTypeClass: z.string().nullable().optional(),
});

export type CreateStatusLifecycleInfo = z.infer<
	typeof CreateStatusLifecycleInfoSchema
>;

/**
 * GET /workflows/status-lifecycles query params (OpenAPI uses `suborganizationId`).
 */
export type StatusLifecyclesQueryParams = {
	entityTypeClass?: string;
	suborganizationId?: string;
	jobTypeId?: string;
	ticketTypeId?: string;
	assetTypeId?: string;
};

/**
 * GET /jobs/status-lifecycles and GET /tickets/status-lifecycles (swagger: subOrganizationId, jobTypeId).
 */
export type ScopedStatusLifecyclesQueryParams = {
	subOrganizationId?: string;
	jobTypeId?: string;
};

export type JobStatusLifecyclesQueryParams = ScopedStatusLifecyclesQueryParams;
export type TicketStatusLifecyclesQueryParams =
	ScopedStatusLifecyclesQueryParams;

/**
 * Query params for GET /workflows/status-lifecycles/{start}/{count},
 * GET /workflows/status-lifecycles/count, and export (swagger Workflow).
 */
export type StatusLifecyclesListParams = {
	IncludeDeleted?: boolean;
	IncludeInActive?: boolean;
	SearchTerm?: string;
	CreatedDate?: string;
	LastUpdatedDate?: string;
	CreatedBefore?: string;
	CreatedAfter?: string;
	UpdatedBefore?: string;
	UpdatedAfter?: string;
	OrganizationId?: string;
	SubOrganizationId?: string;
	ForOrganizationUser?: string;
	"OrderBy.Fields"?: string[];
	"OrderBy.Descending"?: boolean;
	EntityTypeClass?: string;
	jobTypeId?: string;
	ticketTypeId?: string;
	assetTypeId?: string;
};

/** Input for list/count/export — supports camelCase org/entityTypeClass aliases */
export type StatusLifecyclesQueryInput = Partial<StatusLifecyclesListParams> & {
	organizationId?: string;
	subOrganizationId?: string;
	/** camelCase alias — normalized to EntityTypeClass for the API */
	entityTypeClass?: string;
};

export function normalizeStatusLifecyclesListParams(
	input?: StatusLifecyclesQueryInput,
): StatusLifecyclesListParams {
	if (!input) return {};
	const { organizationId, subOrganizationId, entityTypeClass, ...rest } = input;
	const out: StatusLifecyclesListParams = { ...rest };
	if (organizationId !== undefined && organizationId !== "") {
		out.OrganizationId = organizationId;
	}
	if (subOrganizationId !== undefined && subOrganizationId !== "") {
		out.SubOrganizationId = subOrganizationId;
	}
	if (entityTypeClass !== undefined && entityTypeClass !== "") {
		out.EntityTypeClass = entityTypeClass;
	}
	return out;
}

/** OpenAPI `StatusCategoryDisplay` */
export const StatusCategoryDisplaySchema = z
	.object({
		id: z.string(),
		name: z.string().optional(),
		description: z.string().nullable().optional(),
		icon: z.string().nullable().optional(),
		iconColor: z.string().nullable().optional(),
		statusCategoryType: z.string().optional(),
	})
	.passthrough();

export type StatusCategoryDisplay = z.infer<typeof StatusCategoryDisplaySchema>;

/**
 * OpenAPI `StatusDisplay2` — nested in status lifecycle displays
 */
export const StatusDisplay2Schema = z
	.object({
		id: z.string(),
		name: z.string().optional(),
		description: z.string().nullable().optional(),
		/** Flat fields — may be returned alongside or instead of nested statusCategory */
		statusCategoryId: z.string().nullable().optional(),
		statusCategoryName: z.string().nullable().optional(),
		statusCategoryIcon: z.string().nullable().optional(),
		statusCategoryIconColor: z.string().nullable().optional(),
		/** Nested category object per swagger StatusDisplay2 schema */
		statusCategory: StatusCategoryDisplaySchema.nullable().optional(),
	})
	.passthrough();

export type StatusDisplay2 = z.infer<typeof StatusDisplay2Schema>;

/**
 * OpenAPI `StatusTransitionDisplay`
 */
export const StatusTransitionDisplaySchema = z
	.object({
		fromStatus: StatusDisplay2Schema.nullable().optional(),
		toStatus: StatusDisplay2Schema.nullable().optional(),
	})
	.passthrough();
export type StatusTransitionDisplay = z.infer<
	typeof StatusTransitionDisplaySchema
>;

/** OpenAPI `StatusDisplayWithOrder` — GET detail `statuses` items */
export const StatusDisplayWithOrderSchema = z
	.object({
		id: z.string(),
		displayOrder: z.number(),
		name: z.string().optional(),
		description: z.string().nullable().optional(),
		statusCategoryId: z.string().nullable().optional(),
		statusCategoryName: z.string().nullable().optional(),
		statusCategoryIcon: z.string().nullable().optional(),
		statusCategoryIconColor: z.string().nullable().optional(),
		statusCategory: StatusCategoryDisplaySchema.nullable().optional(),
	})
	.passthrough();

export type StatusDisplayWithOrder = z.infer<
	typeof StatusDisplayWithOrderSchema
>;

/**
 * OpenAPI `StatusLifecycleDisplay` — GET /workflows/status-lifecycles items
 * (Runtime payloads may include name, source, organizationId, etc.; optional fields are documented here.)
 */
export const StatusLifecycleDisplaySchema = z
	.object({
		id: z.string(),
		name: z.string().optional(),
		description: z.string().nullable().optional(),
		source: z.string().optional(),
		organizationId: z.string().nullable().optional(),
		/** When true, system/default lifecycle; when false, custom; null falls back to `source` in UI helpers */
		isDefault: z.boolean().nullable().optional(),
		/** Active workflow when true; some APIs may use `active` instead */
		isActive: z.boolean().optional(),
		active: z.boolean().optional(),
		entityTypeClass: EntityTypeClassFieldSchema,
		initialStatus: StatusDisplay2Schema.nullable().optional(),
		/** Detail GET (`StatusLifecycleDisplay2`) — statuses with display order */
		statuses: z.array(StatusDisplayWithOrderSchema).nullable().optional(),
		transitions: z.array(StatusTransitionDisplaySchema).nullable().optional(),
		/** Map of statusId → display order integer */
		displayOrder: z.record(z.string(), z.number()).nullable().optional(),
		// Audit fields (available on detail responses)
		createdBy: z.string().nullable().optional(),
		createdAt: z.string().nullable().optional(),
		createdAtOffset: z.string().nullable().optional(),
		updatedBy: z.string().nullable().optional(),
		updatedAt: z.string().nullable().optional(),
		updatedAtOffset: z.string().nullable().optional(),
		/** Display name strings supplied directly by the backend */
		createdByUser: z.string().nullable().optional(),
		updatedByUser: z.string().nullable().optional(),
	})
	.passthrough();

export type StatusLifecycleDisplay = z.infer<
	typeof StatusLifecycleDisplaySchema
>;

/** Build PUT `displayOrder` from GET `statuses` (preferred); else fallback to `displayOrder` map */
export function displayOrderPayloadFromLifecycle(
	lifecycle: StatusLifecycleDisplay,
): Record<string, number> | null {
	const statuses = lifecycle.statuses;
	if (statuses?.length) {
		const out: Record<string, number> = {};
		for (const s of statuses) {
			if (s.id && typeof s.displayOrder === "number") {
				out[s.id] = s.displayOrder;
			}
		}
		if (Object.keys(out).length > 0) {
			return out;
		}
	}
	const direct = lifecycle.displayOrder;
	return direct && Object.keys(direct).length > 0 ? direct : null;
}

export const StatusLifecycleDisplayArraySchema = z.array(
	StatusLifecycleDisplaySchema,
);

export { ServiceResultSchema, type ServiceResult };
export { AuditLogDisplaySchema, type AuditLogDisplay };

export const StatusTransitionSchema = z.object({
	fromStatusId: z.string(),
	toStatusId: z.string(),
});

export type StatusTransition = z.infer<typeof StatusTransitionSchema>;

export const EditStatusLifecycleInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	initialStatusId: z.string().nullable().optional(),
	displayOrder: z.record(z.string(), z.number()).nullable().optional(),
	validTransitions: z.array(StatusTransitionSchema).nullable().optional(),
});

export type EditStatusLifecycleInfo = z.infer<
	typeof EditStatusLifecycleInfoSchema
>;

/**
 * PUT /workflows/status-lifecycle-details/{id} — name/description only.
 * Used when the caller has `EditStatusLifecycleDetails` but not the broader
 * `EditStatusLifecycle` permission.
 */
export const EditStatusLifecycleDetailsInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
});

export type EditStatusLifecycleDetailsInfo = z.infer<
	typeof EditStatusLifecycleDetailsInfoSchema
>;

export class LifecycleApiError extends Error {
	constructor(
		message: string,
		public readonly cause?: unknown,
		public readonly statusCode?: number,
	) {
		super(message);
		this.name = "LifecycleApiError";
		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, LifecycleApiError);
		}
	}

	static fromServiceResult(result: {
		success: boolean;
		message?: string | null;
	}) {
		return new LifecycleApiError(
			result.message || "Lifecycle operation failed",
			result,
		);
	}
}
